import { cn } from "@/lib/utils";
import { Globe, ExternalLink, Pin } from "lucide-react";
import { labelType } from "@/lib/dossiers";

function inline(text) {
    return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
        part.startsWith("**") ? <strong key={`${i}-${part}`} className="font-semibold text-foreground">{part.slice(2, -2)}</strong> : part
    );
}

export function Markdown({ text }) {
    const lines = text.split("\n");
    return (
        <div className="space-y-2 text-sm leading-relaxed">
            {lines.map((raw, i) => {
                const l = raw.trimEnd();
                if (!l.trim()) return null;
                if (/^#{1,3}\s/.test(l)) {
                    const level = l.match(/^#+/)[0].length;
                    return (
                        <p key={`${i}-${l}`} className={cn("font-display text-foreground pt-3", level === 1 ? "text-2xl" : level === 2 ? "text-xl" : "text-lg")}>
                            {inline(l.replace(/^#+\s*/, ""))}
                        </p>
                    );
                }
                if (/^\s*[-*•]\s/.test(l)) return <p key={`${i}-${l}`} className="pl-5 relative before:content-['–'] before:absolute before:left-1">{inline(l.replace(/^\s*[-*•]\s/, ""))}</p>;
                if (/^\s*\d+[.)]\s/.test(l)) return <p key={`${i}-${l}`} className="pl-5">{inline(l.trim())}</p>;
                return <p key={`${i}-${l}`}>{inline(l)}</p>;
            })}
        </div>
    );
}

export function Sources({ sources }) {
    return (
        <div data-testid="ia-sources" className="mt-4 pt-3 border-t border-border">
            <p className="flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2">
                <Globe className="h-3.5 w-3.5" strokeWidth={1.75} /> Sources web · {sources.length}
            </p>
            <ol className="flex flex-wrap gap-2">
                {sources.map((s, i) => (
                    <li key={s.uri}>
                        <a
                            href={s.uri}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`ia-source-${i}`}
                            className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1 text-xs text-foreground hover:bg-accent hover:border-primary/40 transition-colors"
                        >
                            <span className="text-muted-foreground">[{i + 1}]</span> {s.title}
                            <ExternalLink className="h-3 w-3 text-muted-foreground" strokeWidth={1.75} />
                        </a>
                    </li>
                ))}
            </ol>
        </div>
    );
}

export default function IaMessage({ message: m, streaming, onPin, pinned }) {
    const isUser = m.role === "user";
    if (isUser && m.kind === "synthese") return null;
    return (
        <div
            data-testid={`ia-message-${m.role}`}
            className={cn(
                "rounded-md px-5 py-4",
                isUser ? "bg-primary text-primary-foreground ml-16" : "bg-card border border-border text-foreground"
            )}
        >
            {isUser ? (
                <p className="text-sm">{m.content}</p>
            ) : (
                <>
                    <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2">
                        {m.kind === "synthese" ? `Synthèse financière${m.type_mandat ? ` · ${labelType(m.type_mandat)}` : ""}` : "Réponse"}
                    </p>
                    {m.content ? <Markdown text={m.content} /> : <p className="text-sm text-muted-foreground animate-pulse">Analyse du dossier en cours…</p>}
                    {streaming && m.content && <span className="inline-block w-2 h-4 bg-primary/60 animate-pulse ml-1 align-middle" />}
                    {!streaming && m.sources?.length > 0 && <Sources sources={m.sources} />}
                    {!streaming && onPin && m.content && (
                        <div className="mt-3 flex justify-end">
                            <button
                                type="button"
                                data-testid="pin-insight-button"
                                onClick={() => onPin(m.content)}
                                disabled={pinned}
                                className={cn("inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition-colors",
                                    pinned ? "border-primary/30 bg-accent text-primary cursor-default" : "border-border bg-background text-foreground hover:bg-accent hover:border-primary/40")}
                            >
                                <Pin className="h-3 w-3" strokeWidth={1.75} /> {pinned ? "Épinglé à l'analyse" : "Épingler à l'analyse"}
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
